import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { X, CreditCard, AlertCircle, CheckCircle, Building2 } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const WithdrawModal = ({ balance, onClose, onSuccess }) => {
  const [withdrawType, setWithdrawType] = useState('card');
  const { tt } = useLanguage(); // 'card' или 'account'
  const [formData, setFormData] = useState({
    amount: '',
    card_number: '',
    card_holder: '',
    account_number: '',
    account_holder: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(formData.amount);
    
    // Валидация суммы
    if (amount <= 0) {
      setError('Сумма должна быть больше 0');
      return;
    }
    
    if (amount > balance) {
      setError(`Недостаточно средств. Доступно: $${balance.toFixed(2)}`);
      return;
    }

    // Валидация в зависимости от типа
    if (withdrawType === 'card') {
      if (!formData.card_number || formData.card_number.replace(/\s/g, '').length < 16) {
        setError('Укажите корректный номер карты');
        return;
      }
      if (!formData.card_holder) {
        setError('Укажите имя держателя карты');
        return;
      }
    } else {
      if (!formData.account_number || formData.account_number.length < 8) {
        setError('Укажите корректный номер счёта');
        return;
      }
      if (!formData.account_holder) {
        setError('Укажите имя владельца счёта');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Формируем данные в зависимости от типа вывода
      const requestData = {
        amount,
        withdraw_type: withdrawType
      };

      if (withdrawType === 'card') {
        requestData.card_number = formData.card_number.replace(/\s/g, '');
        requestData.card_holder = formData.card_holder;
      } else {
        requestData.account_number = formData.account_number;
        requestData.account_holder = formData.account_holder;
      }
      
      const response = await fetch(`${getBackendUrl()}/api/withdraw-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          onSuccess();
          onClose();
        }, 3000);
      } else {
        const errorText = await response.text();
        try {
          const data = JSON.parse(errorText);
          setError(data.detail || 'Ошибка при создании заявки');
        } catch {
          setError(`Ошибка сервера (${response.status})`);
        }
      }
    } catch (error) {
      setError('Ошибка соединения. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  // Success message
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
        <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">{tt('Заявка отправлена!')}</h3>
          <p className="text-gray-300 text-sm">{tt('Ваша заявка на вывод средств принята к рассмотрению')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div className="bg-gray-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-bold text-white">{tt('Заявка на вывод средств')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="flex items-center bg-red-500/20 border border-red-500 rounded p-3 text-red-400 text-sm">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Balance Info */}
          <div className="bg-gray-700/50 rounded p-3">
            <div className="text-gray-400 text-sm">{tt('Доступно для вывода')}</div>
            <div className="text-2xl font-bold text-green-400">${balance?.toFixed(2) || '0.00'}</div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1">
              Сумма вывода ($)
            </label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-400 focus:outline-none"
              placeholder="100"
              min="10"
              step="0.01"
              required
            />
          </div>

          {/* Type Selector */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Способ вывода
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setWithdrawType('card')}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded border transition-colors ${
                  withdrawType === 'card'
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                }`}
              >
                <CreditCard className="w-5 h-5" />
                <span>{tt('На карту')}</span>
              </button>
              <button
                type="button"
                onClick={() => setWithdrawType('account')}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded border transition-colors ${
                  withdrawType === 'account'
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                }`}
              >
                <Building2 className="w-5 h-5" />
                <span>{tt('На счёт')}</span>
              </button>
            </div>
          </div>

          {/* Card Fields */}
          {withdrawType === 'card' && (
            <>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  Номер карты
                </label>
                <input
                  type="text"
                  value={formData.card_number}
                  onChange={(e) => setFormData({ ...formData, card_number: formatCardNumber(e.target.value) })}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-400 focus:outline-none font-mono"
                  placeholder="0000 0000 0000 0000"
                  maxLength="19"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  Имя держателя карты
                </label>
                <input
                  type="text"
                  value={formData.card_holder}
                  onChange={(e) => setFormData({ ...formData, card_holder: e.target.value.toUpperCase() })}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-400 focus:outline-none uppercase"
                  placeholder="IVAN IVANOV"
                  required
                />
              </div>
            </>
          )}

          {/* Account Fields */}
          {withdrawType === 'account' && (
            <>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  Номер счёта / IBAN
                </label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value.toUpperCase() })}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-400 focus:outline-none font-mono"
                  placeholder="GE29NB0000000101904917"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  ФИО владельца счёта
                </label>
                <input
                  type="text"
                  value={formData.account_holder}
                  onChange={(e) => setFormData({ ...formData, account_holder: e.target.value.toUpperCase() })}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-400 focus:outline-none uppercase"
                  placeholder={tt('ИВАНОВ ИВАН ИВАНОВИЧ')}
                  required
                />
              </div>
            </>
          )}

          {/* Info */}
          <div className="bg-yellow-600/20 border border-yellow-600 rounded p-2">
            <div className="text-yellow-400 text-xs">
              <div className="font-medium">{tt('Важно:')}</div>
              <div>{tt('Заявки обрабатываются в рабочие дни • Минимум: $10')}</div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
            >
              {loading ? 'Отправка...' : 'Подать заявку'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WithdrawModal;
